// useAgreementStatusTransitions.js

import { useState, useCallback } from "react";
import { toast } from "react-toastify";

/**
 * Custom hook to manage the state and logic for User Agreement status transitions.
 * @param {function} updateAgreementStatus - API function to update the status.
 * @param {function} refetch - Function to refresh the agreement list.
 * @returns {object} Status transition state, handlers, and utility functions.
 */
const useAgreementStatusTransitions = (updateAgreementStatus, refetch) => {
    // State for Status Change Modal
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusChangeData, setStatusChangeData] = useState({ id: null, newStatus: null, currentStatus: null, version: null });

    // --- Core Status Transition Logic ---

    const handleStatusChange = useCallback(async (id, newStatus) => {
        if (!updateAgreementStatus) {
            toast.error("Status update function is not available.");
            return;
        }

        try {
            await updateAgreementStatus(id, newStatus);
            toast.success(`Agreement status updated to ${newStatus}`);
            await refetch();
        } catch (err) {
            console.error("Failed to update User Agreement status:", err);
            toast.error(`Failed to update status: ${err.message || 'Server error'}`);
        } finally {
            setShowStatusModal(false);
            // Reset statusChangeData
            setStatusChangeData({ id: null, newStatus: null, currentStatus: null, version: null }); 
        }
    }, [updateAgreementStatus, refetch]);


    const confirmStatusChange = useCallback((id, newStatus, currentStatus, version) => {
        // Show modal for critical actions (Publishing/Retiring)
        if (newStatus === 'ACTIVE' || newStatus === 'EXPIRED' || newStatus === 'DELETED') {
            // Save version to state for use in the modal helper
            setStatusChangeData({ id, newStatus, currentStatus, version }); 
            setShowStatusModal(true);
        } else {
            // For non-critical changes (DRAFT <-> READY, etc.), call the handler directly
            handleStatusChange(id, newStatus);
        }
    }, [handleStatusChange]);


    // --- Status Transition Configuration ---

    const getValidTransitions = useCallback((currentStatus) => {
        switch (currentStatus) {
            case 'DRAFT':
                return [
                    { label: 'Set to READY', targetStatus: 'READY' },
                    { label: 'Set to ABANDONED', targetStatus: 'ABANDONED' },
                ];
            case 'READY':
                return [
                    { label: 'Activate', targetStatus: 'ACTIVE' },
                    { label: 'Return to DRAFT', targetStatus: 'DRAFT' },
                ];
            case 'ACTIVE':
                return [
                    { label: 'Retire Agreement', targetStatus: 'EXPIRED' },
                ];
            case 'EXPIRED':
                return [
                    { label: 'Delete', targetStatus: 'DELETED' },
                ];
            case 'ABANDONED':
                return [
                    { label: 'Restore to DRAFT', targetStatus: 'DRAFT' },
                ];
            case 'DELETED':
                // Note: The main component handles the delete confirmation separately, 
                // but this transition might be used for 'hard delete' or 'restore' logic.
                return [
                    { label: 'Restore', targetStatus: 'DRAFT' },
                ];
            default:
                return [];
        }
    }, []);

    // --- Modal Content Generation ---

    const getStatusModalContent = useCallback((newStatus, currentStatus, version) => {
        const displayVersion = version || 'N/A';
        
        switch (newStatus) {
            case 'ACTIVE':
                return {
                    title: "Confirm Publication & Activation",
                    body: `Activating agreement version ${displayVersion} will retire the currently ACTIVE agreement by setting its status to EXPIRED. This is a critical legal action. Are you sure you want to proceed?`,
                    confirmVariant: "success",
                    confirmLabel: "Confirm"
                };
            case 'EXPIRED':
                return {
                    title: "Confirm Agreement Retirement",
                    body: `Retiring agreement version ${displayVersion} will immediately set its status to EXPIRED, making it invalid for new signups. Are you sure you want to retire this agreement?`,
                    confirmVariant: "danger",
                    confirmLabel: "Retire"
                };
            case 'DELETED':
                return {
                    title: "Confirm Delete",
                    body: `The agreement version ${displayVersion} will be deleted. Are you sure you want to continue?`,
                    confirmVariant: "danger",
                    confirmLabel: "Delete"
                };
            default:
                return {
                    title: `Confirm Status Change`,
                    body: `Are you sure you want to change the status of agreement version ${displayVersion} from ${currentStatus} to ${newStatus}?`,
                    confirmVariant: "primary",
                    confirmLabel: "Confirm Change"
                };
        }
    }, []); 

    // --- Utility Functions ---

    const isEditable = useCallback((status) => {
        return status === 'DRAFT' || status === 'READY';
    }, []);

   


    return {
        showStatusModal, 
        setShowStatusModal, 
        statusChangeData,
        confirmStatusChange, 
        handleStatusChange,
        getStatusModalContent,
        getValidTransitions,
        isEditable,
    };
};

export default useAgreementStatusTransitions;